/* ==========================================================
   KD custom script, PART 2 (kd-custom-2.js)
   Started Aug 27, 2026. Loaded from layout/theme.liquid, right
   at the bottom of the page, with defer -- so it never delays
   anything loading and always runs after the page is built.

   Why this file exists at all: everything else in this theme is
   done with CSS, which is safer and simpler. But the Etsy
   reviews widget is drawn by the Reputon app's own JavaScript
   AFTER the page arrives, and the two things Karla asked for on
   Aug 27 cannot be expressed in CSS:

     1) hide the "AI summary" card (the one that says "Based on
        29 reviews", which argues with the "Most Recent of 24K+
        Raving Reviews" heading right above it). CSS cannot
        select an element by the words inside it -- only by class
        name, and Reputon's class names are generated and can
        change under us. Matching the WORDS is the stable way.

     2) keep the reviews carousel moving on its own. The app's
        own "autoscroll" switch is already ON in the theme
        settings, so something downstream is stopping it -- most
        likely the carousel's own "stop autoplay once the visitor
        touches it" behaviour, which never restarts. Rather than
        guess at the cause, this nudges the widget's own next
        arrow on a timer, which works whatever the cause.

   TO TURN THE WHOLE THING OFF: add this line above the script
   tag in layout/theme.liquid --  <script>window.KD_REVIEWS_FIX =
   false;</script>  -- or just delete the script tag.

   Sep 3 addition, 3): the summary card turned out to be the
   carousel's FIRST slide. Hiding it left an empty first slot on
   screen until the 6-second timer moved things along, which is
   what Karla saw as the first review "sliding in" late. So the
   moment the card is hidden, the carousel is nudged forward once
   right away (a zero-length jump on a Swiper, otherwise a click
   on the widget's own next arrow), and the nudge is retried on
   later redraws until it lands, since the arrows can appear a
   beat after the card does.

   Everything below is wrapped in try/catch and bails out quietly
   if the widget is not on the page or does not look the way we
   expect. It can slow the page down or break it only if it
   throws, and it cannot throw.
   ========================================================== */
(function () {
  'use strict';

  if (window.KD_REVIEWS_FIX === false) return;

  /* ---- Dials ------------------------------------------------
     SLIDE_MS      how long each review sits before the next one
                   slides in (6000 = 6 seconds; 4000 is brisk,
                   9000 is leisurely).
     GIVE_UP_MS    how long to keep waiting for the widget to
                   draw itself before giving up (it normally
                   appears within 2-3 seconds).
     CARD_MAX_TEXT the safety belt on hiding: a container holding
                   more than this many characters is assumed to
                   be the reviews themselves, not the little
                   summary card, and is left alone.
     ---------------------------------------------------------- */
  var SLIDE_MS = 6000;
  var GIVE_UP_MS = 25000;
  var CARD_MAX_TEXT = 600;

  /* The reviews section. The id Shopify prints starts with a
     template prefix that changes every time the theme is copied,
     so we match the tail -- the same trick the CSS file uses. */
  var SECTION_SELECTOR =
    'div[id^="shopify-section"][id$="__174261012156157954"]';

  /* The words on the card we want gone. Written loosely on
     purpose: "Based on 29 reviews", "based on 1,204 reviews",
     "Based On 29 Reviews" all match. */
  var SUMMARY_RE = /based\s+on\s+[\d.,]+\s+reviews?/i;

  /* A second net, in case Reputon labels the card rather than
     counting reviews on it. */
  var AI_HEADING_RE =
    /^\s*(ai[\s\-–—]*(generated\s+)?(review\s+)?summary|review[s]?\s+summary|summary\s+of\s+(the\s+)?reviews)\s*$/i;

  /* Things that look like a "next" control inside the widget.
     Deliberately broad -- the scoring below decides which one. */
  var NEXT_SELECTOR = [
    '[class*="arrow-right"]',
    '[class*="arrow_right"]',
    '[class*="arrowRight"]',
    '[class*="next"]',
    '[class*="arrow"]',
    'button[class*="right"]'
  ].join(',');

  /* Class names that mean "this is one of the reviews itself",
     used to make sure we never hide a real review that happens to
     contain the words we are hunting for.

     Matched as whole words on purpose. The obvious version of
     this -- looking for "slide" anywhere in the class name --
     backfires: a carousel's outer wrapper is usually called
     something like "reviews-slider", and "slider" CONTAINS
     "slide", so every match on the page would look like it was
     inside a review and the summary card would never be hidden.
     These compound names cannot collide that way. */
  var CARD_CLASS_RE =
    /(^|[^a-z])(swiper-slide|review-card|review-item|review__item|reviews__item)([^a-z]|$)/;

  function classOf(el) {
    /* SVG elements have an SVGAnimatedString, not a string. */
    var c = el.className;
    if (c && typeof c === 'object' && 'baseVal' in c) c = c.baseVal;
    return String(c || '').toLowerCase();
  }

  function textOf(el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /* -----------------------------------------------------------
     1) The AI summary card
     -----------------------------------------------------------
     Find the SMALLEST element that contains the phrase (the one
     with no child that also contains it), then walk outwards to
     the card it sits in, stopping the moment the thing we are
     standing on starts to look like the whole widget rather than
     one little card. Then hide that.
     ---------------------------------------------------------- */

  function smallestMatches(root, re) {
    var out = [];
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.hasAttribute('data-kd-hidden')) continue;
      if (!re.test(textOf(el))) continue;
      /* if a child also matches, the child is the better target */
      var deeper = false;
      for (var j = 0; j < el.children.length; j++) {
        if (re.test(textOf(el.children[j]))) { deeper = true; break; }
      }
      if (!deeper) out.push(el);
    }
    return out;
  }

  function cardAround(el, root) {
    var rootLen = textOf(root).length || 1;
    var best = el;
    var node = el;
    var steps = 0;

    while (steps < 5) {
      var parent = node.parentElement;
      if (!parent || parent === root || !root.contains(parent)) break;

      var t = textOf(parent);
      if (t.length > CARD_MAX_TEXT) break;        /* too much text  */
      if (t.length > rootLen * 0.5) break;        /* half the widget */
      if (parent.querySelector(NEXT_SELECTOR)) break; /* holds the arrows */
      if (parent.children.length > 8) break;      /* looks like a list */

      best = parent;
      node = parent;
      steps++;
    }
    return best;
  }

  function insideAReview(el, root) {
    var node = el.parentElement;
    while (node && node !== root && root.contains(node)) {
      if (CARD_CLASS_RE.test(classOf(node))) return true;
      node = node.parentElement;
    }
    return false;
  }

  function hide(el, why) {
    if (!el || el.hasAttribute('data-kd-hidden')) return false;
    el.setAttribute('data-kd-hidden', why);
    el.classList.add('kd-hidden');
    el.style.setProperty('display', 'none', 'important');
    return true;
  }

  function hideSummary(root) {
    var hidden = 0;
    var i;

    var hits = smallestMatches(root, SUMMARY_RE);
    for (i = 0; i < hits.length; i++) {
      /* a review that happens to say "based on 3 reviews" stays */
      if (insideAReview(hits[i], root)) continue;
      if (hide(cardAround(hits[i], root), 'summary-count')) hidden++;
    }

    var labels = smallestMatches(root, AI_HEADING_RE);
    for (i = 0; i < labels.length; i++) {
      if (insideAReview(labels[i], root)) continue;
      if (hide(cardAround(labels[i], root), 'summary-label')) hidden++;
    }

    return hidden;
  }

  /* -----------------------------------------------------------
     2) Keeping the carousel moving
     -----------------------------------------------------------
     Pick the widget's own next arrow and click it on a timer.
     Pauses while the visitor is hovering or touching it, while
     the section is off screen, and while the browser tab is in
     the background -- so it never fights the person reading.
     ---------------------------------------------------------- */

  function pickNext(root) {
    var cands = root.querySelectorAll(NEXT_SELECTOR);
    var best = null;
    var bestScore = 0;

    for (var i = 0; i < cands.length; i++) {
      var el = cands[i];
      var cls = classOf(el);

      /* "copyright" contains "right" -- not an arrow. */
      if (cls.indexOf('copyright') !== -1) continue;
      /* a real link would navigate away if we clicked it */
      if (el.tagName === 'A') {
        var href = el.getAttribute('href') || '';
        if (href && href !== '#' && href.slice(0, 11) !== 'javascript:') continue;
      }

      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) continue;          /* hidden */
      if (r.width > 200 || r.height > 200) continue; /* too big to be an arrow */
      if (el.disabled) continue;

      var score = 0;
      if (/next/.test(cls)) score += 4;
      if (/right/.test(cls)) score += 3;
      if (/arrow/.test(cls)) score += 2;
      if (el.tagName === 'BUTTON') score += 1;
      if (score > bestScore) { bestScore = score; best = el; }
    }
    return best;
  }

  /* Move to the next slide right now, no waiting for the timer.
     Returns true if something was there to move. */
  function advanceNow(root) {
    try {
      var sw = root.querySelector('[class*="swiper"]');
      if (sw && sw.swiper && typeof sw.swiper.slideNext === 'function') {
        sw.swiper.slideNext(0);
        return true;
      }
    } catch (e) { /* not a Swiper */ }
    try {
      var btn = pickNext(root);
      if (btn) { btn.click(); return true; }
    } catch (e) { /* fall through */ }
    return false;
  }

  function startAutoplay(root) {
    if (root.hasAttribute('data-kd-autoplay')) return;
    root.setAttribute('data-kd-autoplay', '1');

    var paused = false;
    var onScreen = true;
    var resume = null;

    function holdFor(ms) {
      paused = true;
      clearTimeout(resume);
      resume = setTimeout(function () { paused = false; }, ms);
    }

    root.addEventListener('mouseenter', function () { paused = true; }, true);
    root.addEventListener('mouseleave', function () { paused = false; }, true);
    root.addEventListener('touchstart', function () { holdFor(SLIDE_MS * 2); }, true);
    root.addEventListener('click', function () { holdFor(SLIDE_MS * 2); }, true);

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
      }, { threshold: 0.2 }).observe(root);
    }

    /* If the widget happens to be a Swiper, ask it nicely first --
       a restarted native autoplay is smoother than clicking. */
    try {
      var sw = root.querySelector('[class*="swiper"]');
      if (sw && sw.swiper && sw.swiper.autoplay &&
          typeof sw.swiper.autoplay.start === 'function') {
        if (sw.swiper.params && sw.swiper.params.autoplay) {
          sw.swiper.params.autoplay.disableOnInteraction = false;
        }
        sw.swiper.autoplay.start();
      }
    } catch (e) { /* not a Swiper; the timer below covers it */ }

    setInterval(function () {
      try {
        if (paused || !onScreen || document.hidden) return;
        var btn = pickNext(root);
        if (btn) btn.click();
      } catch (e) { /* never let a bad tick break the page */ }
    }, SLIDE_MS);
  }

  /* -----------------------------------------------------------
     Wiring: the widget draws itself late and can redraw itself,
     so watch the section and re-apply until it settles.
     ---------------------------------------------------------- */

  function boot() {
    var root;
    try {
      root = document.querySelector(SECTION_SELECTOR);
    } catch (e) { return; }
    if (!root) return;                 /* not the homepage */

    var started = Date.now();
    var settled = null;

    /* Set when the summary card gets hidden; cleared once the
       carousel has actually been moved off that empty slot. */
    var skipPending = false;

    function skipEmptySlot() {
      if (!skipPending) return;
      if (advanceNow(root)) skipPending = false;
    }

    function pass() {
      try {
        if (textOf(root).length < 20) return false;  /* not drawn yet */
        if (hideSummary(root) > 0) skipPending = true;
        startAutoplay(root);
        skipEmptySlot();
        return true;
      } catch (e) {
        return true;                   /* stop trying on error */
      }
    }

    pass();

    /* Re-run on every redraw, throttled. */
    if (window.MutationObserver) {
      var queued = false;
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () {
          queued = false;
          try {
            if (hideSummary(root) > 0) skipPending = true;
            startAutoplay(root);
            skipEmptySlot();
          } catch (e) {}
        }, 250);
      });
      mo.observe(root, { childList: true, subtree: true });

      /* Stop watching once things have been quiet for a while. */
      settled = setInterval(function () {
        if (Date.now() - started > GIVE_UP_MS) {
          clearInterval(settled);
          mo.disconnect();
        }
      }, 1000);
    } else {
      /* Very old browser: just poll for a bit. */
      var poll = setInterval(function () {
        if (pass() || Date.now() - started > GIVE_UP_MS) clearInterval(poll);
      }, 500);
    }
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  } catch (e) { /* nothing here is worth breaking a page over */ }
})();
